import { Image } from '@chakra-ui/react';
import styled from '@emotion/styled';
import emojiRegexLib from 'emoji-regex';
import linkifyIt from 'linkify-it';
import { Link as ReactRouterLink, useHistory } from 'react-router-dom';
import { EmojiFontCSS } from 'styles/common';
import { baseTheme } from 'themes/clients/baseTheme';
import tlds from 'tlds';

import quote from 'assets/assetsV1/icons/Quote.svg';

import { getColorFromKey, getProfileRoute, goToProfilePage, goToSearchDetailPage } from 'constants/helper';

import { getPrefixAndContent } from './helpers';

const linkify = linkifyIt();
linkify.tlds(tlds);
const emojiRegex = emojiRegexLib();

const StyledLink = styled(ReactRouterLink)`
  text-decoration: none;
  cursor: pointer;
`;

function ParsedComponent({ className, prefix, content, data, history, onClick, hrefTo = null }) {
  if (hrefTo) {
    return (
      <StyledLink to={hrefTo}>
        <ContentSpan onClick={() => {}} className={className}>
          {content === undefined ? (
            ''
          ) : (
            <>
              {prefix}
              {content}
            </>
          )}
        </ContentSpan>
      </StyledLink>
    );
  }
  return (
    <ContentSpan onClick={onClick} className={className}>
      {/* 
      {/* 
        {/* 
          Adding undefined content check here. if content is undefined then don't show prefix as well.
        */}
      {content === undefined ? (
        ''
      ) : (
        <>
          {prefix}
          {content}
        </>
      )}
    </ContentSpan>
  );
}

const ResultComp = ({ text = '', res }) => {
  const history = useHistory();
  // ✅ Matches placeholders inside {{...}}, optionally prefixed by #, @, or +.
  // - Prevents catastrophic backtracking by avoiding `.*?` inside curly braces.
  // - Ensures only non-nested placeholders are matched.
  const macrosDisplayPattern = /([#@+]?\{\{[^{}]*\}\})/gm;

  // ✅ Matches placeholders inside {{...}}, ensuring no nested braces.
  // - Prevents performance issues by limiting what can be inside {{...}}.
  const replacedLinkPattern = /\{\{[^{}]*\}\}/gm;

  // const emojiRegex = /\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff]/gm;
  // combination of macro Regex and emoji regex:
  // We are using brackets, otherwise the content gets removed when we split, but we want that particular content to be able to replace it
  const newPattern = new RegExp(`${macrosDisplayPattern.source}|(${emojiRegex.source})`);
  // const newPattern = /(.?{{.*?}})|(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/gm;
  const parts = text?.split(newPattern);
  // remove leading and ending double curly braces

  const result = [];
  const partsIterator = ({ groupMatch, index }) => {
    // match doesn't exist, it is undefined or null, we just return and do nothing in that case
    if (!groupMatch) return;
    // if the current split part is an emoji, then we replace it with emoji span
    if (emojiRegex.test(groupMatch)) {
      result.push(<Emoji role="img">{groupMatch}</Emoji>);
      return;
    }
    // if the current text doesn't match emoji or macros, we check whether it contains any link
    if (!newPattern.test(groupMatch)) {
      // const llf = linkify;
      // getting all the links using the library which draft js uses under the hood
      const links = linkify.match(groupMatch) || [];

      // because if link is not present, we can just straight away add the string to our result
      if (links.length > 0) {
        /* link is an object with keys: raw, text, url
             for every link we are replacing it with a value that we can easily split
             on later using regex. Also adding index so that we can figure out at what position
             it was inside our link */
        links.forEach((link, index) => {
          groupMatch = groupMatch.split(link.raw).join(`{{myAppLink.${index}}}`);
        });
        // splitting on the link pattern so that we can get our link inside our array
        const parts = groupMatch.split(replacedLinkPattern) ?? [];
        parts?.forEach((part) => {
          // if this is the part which matches our link
          if (part?.match(replacedLinkPattern)) {
            // remove extra data
            part = part.replace('{{', '');
            part = part.replace('}}', '');
            const linkIndex = part.split('.')[1];
            // adding link tag to our result
            result.push(
              <Link target="_blank" href={links[linkIndex].url}>
                {links[linkIndex].raw}
              </Link>
            );
          } else {
            result.push(part); // otherwise this is a normal text, just push it as it is
          }
        });
      } else {
        result.push(groupMatch);
      }
      return;
    }
    getResult({ groupMatch, index });
  };

  const getResult = ({ groupMatch, index }) => {
    const prefixAndContent = getPrefixAndContent({ res, groupMatch });
    const { tagType } = prefixAndContent;
    switch (prefixAndContent.className) {
      case 'mentionComponent':
      case 'employeeTag': {
        if (tagType === 'employees') {
          result.push(
            <ParsedComponent
              onClick={() => goToProfilePage({ history, employeeId: prefixAndContent.data.uuid })}
              key={`textEmployeeComponent${index}`}
              hrefTo={getProfileRoute(prefixAndContent.data.uuid)}
              {...prefixAndContent}
            />
          );
        } else {
          result.push(
            <ParsedComponent
              onClick={() =>
                goToSearchDetailPage({
                  history,
                  searchType: prefixAndContent.data.filterTagType,
                  typeId: prefixAndContent.data.uuid,
                  label: prefixAndContent.data.displayName,
                })
              }
              key={`textEmployeeComponent${index}`}
              {...prefixAndContent}
              className="demographicsComponent"
            />
          );
        }
        break;
      }
      case 'strengthTag':
        result.push(
          <ParsedComponent
            // onClick={() =>
            //   goToSearchDetailPage({
            //     history,
            //     searchType: SEARCH_TAG_TYPES.tag,
            //     typeId: prefixAndContent.data.uuid,
            //     label: prefixAndContent.data.name
            //   })
            // }
            key={`tagComponent${index}`}
            {...prefixAndContent}
          />
        );
        break;
      case 'demographicsTaskDetailsTag':
        result.push(
          <strong>
            {' '}
            {/* to parse macros in task name */}
            <TextParseComponent res={res} text={prefixAndContent.data} weight={600} color={baseTheme.colors.text.g1} />
          </strong>
        );
        break;
      case 'customDateDisplayTag':
        result.push(<strong> {prefixAndContent.data}</strong>);
        break;

      default: {
        result.push(<ParsedComponent key={`textComponent${index}`} {...prefixAndContent} />);
      }
    }
  };

  parts?.forEach((groupMatch, index) => partsIterator({ groupMatch, index }));

  if (!result?.length) {
    return null;
  }
  return result;
};

const ContentSpan = styled.span`
  &.mentionComponent {
    color: ${(props) => props.theme.colors.primary['1565BC']};
    cursor: pointer;
    hyphens: auto;
    word-break: break-word;
    overflow-wrap: anywhere;
    font-weight: 600;
  }
  &.strengthTag {
    font-weight: 600;
  }
  &.employeeTag {
    color: ${(props) => props.theme.colors.primary['1565BC']};
    font-weight: 600;
    &:hover {
      text-decoration: underline;
    }
  }
  cursor: ${(props) => (props.onClick ? 'pointer' : 'default')};
`;
const Link = styled.a`
  color: ${(props) => props.theme.colors.primary['1565BC']};
  cursor: pointer;
  hyphens: auto;
  word-break: break-word;
  overflow-wrap: anywhere;
`;

const Emoji = styled.span`
  ${EmojiFontCSS};
`;

// because parsed components are inside span
// and the text which was normal and wasn't any mention or tag
// that come just as it is
// so they all needed a container to apply white-space: pre-line
// style to them so that the text breaks on \n character
const ContainerSpan = styled.span`
  white-space: ${(props) => props.whiteSpace ?? 'pre-line'};
  hyphens: auto;
  word-break: break-word;
  overflow-wrap: anywhere;
  text-decoration: ${(props) => (props.isStrikedThrough ? 'line-through' : 'none')};

  color: ${(props) => (props.isGreyedOut ? props.theme.colors.neutral['999999'] : getColorFromKey(props.color))};

  font-weight: ${(props) => props.weight};
`;

const MultiLineTruncateContainer = styled.span`
  white-space: normal;
  text-decoration: ${(props) => (props.isStrikedThrough ? 'line-through' : 'none')};

  color: ${(props) => (props.isGreyedOut ? props.theme.colors.neutral['999999'] : getColorFromKey(props.color))};

  font-weight: ${(props) => props.weight};
`;

const TextParseComponent = ({
  text = '',
  macros,
  color = 'textPrimary',
  weight = 'normal',
  isStrikedThrough,
  isGreyedOut,
  whiteSpace = null,
  isMultiLineTruncate = false,
}) => (
  <>
    {isMultiLineTruncate ? (
      <MultiLineTruncateContainer
        whiteSpace={whiteSpace}
        color={color}
        weight={weight}
        isStrikedThrough={isStrikedThrough}
        isGreyedOut={isGreyedOut}
      >
        <ResultComp text={text} res={macros} />
      </MultiLineTruncateContainer>
    ) : (
      <ContainerSpan
        whiteSpace={whiteSpace}
        color={color}
        weight={weight}
        isStrikedThrough={isStrikedThrough}
        isGreyedOut={isGreyedOut}
      >
        <ResultComp text={text} res={macros} />
      </ContainerSpan>
    )}
  </>
);

const QuoteImage = ({ isEndQuote }) => (
  <span
    style={{
      display: 'inline-block',
      transform: `rotate(${isEndQuote ? '180deg' : '0deg'})`,
      userSelect: 'none',
      marginRight: isEndQuote ? '0px' : '2px',
      marginLeft: isEndQuote ? '2px' : '0px',
    }}
  >
    <Image src={quote} maxW="none" />
  </span>
);

const ResultCompForPraise = ({ text = '', res }) => {
  const history = useHistory();
  const macrosDisplayPattern = new RegExp(/([#@+]?{{.*?}})/gm);
  const replacedLinkPattern = /({{.*?}})/gm;
  // const emojiRegex = /\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff]/gm;
  // combination of macro Regex and emoji regex:
  // We are using brackets, otherwise the content gets removed when we split, but we want that particular content to be able to replace it
  const newPattern = new RegExp(`${macrosDisplayPattern.source}|(${emojiRegex.source})`);
  // const newPattern = /(.?{{.*?}})|(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/gm;
  const parts = text?.split(newPattern);
  // remove leading and ending double curly braces

  let result = [];
  const partsIterator = ({ groupMatch, index }) => {
    // match doesn't exist, it is undefined or null, we just return and do nothing in that case
    if (!groupMatch) return;
    // if the current split part is an emoji, then we replace it with emoji span
    if (emojiRegex.test(groupMatch)) {
      result.push(<Emoji role="img">{groupMatch}</Emoji>);
      return;
    }
    // if the current text doesn't match emoji or macros, we check whether it contains any link
    if (!newPattern.test(groupMatch)) {
      // const llf = linkify;
      // getting all the links using the library which draft js uses under the hood
      const links = linkify.match(groupMatch) || [];

      // because if link is not present, we can just straight away add the string to our result
      if (links.length > 0) {
        /* link is an object with keys: raw, text, url
             for every link we are replacing it with a value that we can easily split
             on later using regex. Also adding index so that we can figure out at what position
             it was inside our link */
        links.forEach((link, index) => {
          groupMatch = groupMatch.split(link.raw).join(`{{myAppLink.${index}}}`);
        });
        // splitting on the link pattern so that we can get our link inside our array
        const parts = groupMatch.split(replacedLinkPattern) ?? [];
        parts?.forEach((part) => {
          // if this is the part which matches our link
          if (part?.match(replacedLinkPattern)) {
            // remove extra data
            part = part.replace('{{', '');
            part = part.replace('}}', '');
            const linkIndex = part.split('.')[1];
            // adding link tag to our result
            result.push(
              <Link target="_blank" href={links[linkIndex].url}>
                {links[linkIndex].raw}
              </Link>
            );
          } else {
            result.push(part); // otherwise this is a normal text, just push it as it is
          }
        });
      } else {
        result.push(groupMatch);
      }
      return;
    }
    getResult({ groupMatch, index });
  };

  const getResult = ({ groupMatch, index }) => {
    const prefixAndContent = getPrefixAndContent({ res, groupMatch });
    const { tagType } = prefixAndContent;
    switch (prefixAndContent.className) {
      case 'mentionComponent':
      case 'employeeTag': {
        if (tagType === 'employees') {
          result.push(
            <ParsedComponent
              onClick={() => goToProfilePage({ history, employeeId: prefixAndContent.data.uuid })}
              key={`textEmployeeComponent${index}`}
              hrefTo={getProfileRoute(prefixAndContent.data.uuid)}
              {...prefixAndContent}
            />
          );
        } else {
          result.push(
            <ParsedComponent
              onClick={() =>
                goToSearchDetailPage({
                  history,
                  searchType: prefixAndContent.data.filterTagType,
                  typeId: prefixAndContent.data.uuid,
                  label: prefixAndContent.data.displayName,
                })
              }
              key={`textEmployeeComponent${index}`}
              {...prefixAndContent}
              className="demographicsComponent"
            />
          );
        }
        break;
      }
      case 'strengthTag':
        result.push(
          <ParsedComponent
            // onClick={() =>
            //   goToSearchDetailPage({
            //     history,
            //     searchType: SEARCH_TAG_TYPES.tag,
            //     typeId: prefixAndContent.data.uuid,
            //     label: prefixAndContent.data.name
            //   })
            // }
            key={`tagComponent${index}`}
            {...prefixAndContent}
          />
        );
        break;
      case 'demographicsTaskDetailsTag':
        result.push(
          <strong>
            {' '}
            {/* to parse macros in task name */}
            <TextParseComponent res={res} text={prefixAndContent.data} weight={600} color={baseTheme.colors.text.g1} />
          </strong>
        );
        break;
      case 'customDateDisplayTag':
        result.push(<strong> {prefixAndContent.data}</strong>);
        break;

      default: {
        result.push(<ParsedComponent key={`textComponent${index}`} {...prefixAndContent} />);
      }
    }
  };

  parts?.forEach((groupMatch, index) => partsIterator({ groupMatch, index }));

  if (!result?.length) {
    return null;
  }

  result = [...result, <QuoteImage isEndQuote />];

  return result;
};

const PublicPraiseText = ({ text = '', macros, whiteSpace, color, weight, isStrikedThrough, isGreyedOut }) => (
  <>
    <QuoteImage />
    <ContainerSpan
      whiteSpace={whiteSpace}
      color={color}
      weight={weight}
      isStrikedThrough={isStrikedThrough}
      isGreyedOut={isGreyedOut}
    >
      <ResultCompForPraise text={text} res={macros} />
    </ContainerSpan>
  </>
);

export { TextParseComponent, PublicPraiseText };
